import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { Modal, Button, Avatar, List, Tag, Popconfirm, Tooltip, Flex, Text, Divider, message, useBreakpoint, Icon, Input, Skeleton } from '@nula/core-ui';
import { apiFetch } from '@/lib/http/client';
import type { PagedResponse } from '@/lib/http/types';
import { PermissionSelect } from './permission-select';
import { PendingInvitationsList, type PendingInvitation } from './pending-invitations-list';
import { useAccess } from '@/lib/sharing/hooks/use-access';
import { canManage, canRemoveSelf, PERMISSION_LABEL } from '@/lib/sharing/utils/permissions';
import { shareBaseUrl } from '@/lib/sharing/utils/share-url';
import { isDesktop } from '@/lib/platform';
import { useAuthStore } from '@/stores/use-auth-store';
import { useWorkspaceService } from '@/lib/workspace/use-workspace-service';
import styles from './share-dialog.module.css';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Permission {
  id: string;
  userId: string;
  email: string;
  name?: string;
  avatarUrl?: string;
  permission: string;
  isOwner: boolean;
}

interface ShareLink {
  token: string;
  url: string;
  permission: string;
}

interface PermissionsResponse {
  permissions: Permission[];
  shareLink: ShareLink | null;
}

function removeSelfConfirmDescription(resourceType: 'document' | 'collection' | 'recording', itemCount: number | null): string {
  const noun = resourceType === 'collection' ? 'collection' : resourceType;
  if (resourceType === 'collection' && itemCount) {
    return `You will lose access to this collection and the ${itemCount} item${itemCount === 1 ? '' : 's'} inside it.`;
  }
  return `You will lose access to this ${noun}.`;
}

export interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  /** ID of the document or collection */
  resourceId: string;
  /** Display title */
  resourceTitle: string;
  /**
   * Type of resource — drives the collection notice / share-link suppression
   * for collections. The /entities/<id>/* API paths are kind-agnostic, so
   * recordings work without further branching.
   */
  resourceType?: 'document' | 'collection' | 'recording';
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * ShareDialog — modal for managing document permissions.
 *
 * Sections:
 * 1. Add people: email input + permission select + Invite button
 * 2. People with access: list of collaborators with permission dropdowns
 * 3. Share link: create/disable public link + copy URL
 */
export function ShareDialog({ open, onClose, resourceId, resourceTitle, resourceType = 'document' }: ShareDialogProps) {
  const bp = useBreakpoint();
  const modalWidth: number | string = bp.md ? 520 : '100vw';

  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [shareLink, setShareLink] = useState<ShareLink | null>(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [invitePermission, setInvitePermission] = useState('read');
  const [inviting, setInviting] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [revoking, setRevoking] = useState<string | null>(null);

  const isCollection = resourceType === 'collection'; // retained for renderer logic only
  const basePath = `/entities/${resourceId}`;

  // Gate manage controls on the viewer's own access level.
  // Defaults to true (manages allowed) when no access row is present, so
  // the dialog stays backward-compatible in cases where the entity store
  // hasn't loaded the row yet (e.g. share-link viewers).
  const currentUserEmail = useAuthStore((s) => s.user?.email ?? null);
  const viewerAccess = useAccess(resourceId);
  const viewerCanManage = viewerAccess ? canManage(viewerAccess) : true;
  const workspaceService = useWorkspaceService();

  const fetchPermissions = useCallback(async () => {
    if (!resourceId) return;
    setLoading(true);
    try {
      const [data, pagedInvitations] = await Promise.all([
        apiFetch<PermissionsResponse>(`${basePath}/permissions`),
        apiFetch<PagedResponse<PendingInvitation>>(`${basePath}/invitations`).catch(() => ({ items: [] as PendingInvitation[], total: 0, limit: 50, offset: 0 })),
      ]);
      setPermissions(data.permissions ?? []);
      setShareLink(data.shareLink ?? null);
      setPendingInvitations(pagedInvitations.items ?? []);
    } catch (err) {
      console.error('[ShareDialog] fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [resourceId, basePath]);

  useEffect(() => {
    if (open) {
      fetchPermissions();
    }
  }, [open, fetchPermissions]);

  // ── Invite ────────────────────────────────────────────────────────────────

  const handleInvite = async () => {
    if (!email.trim()) return;
    setInviting(true);
    try {
      await apiFetch(`${basePath}/invitations`, {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), permission: invitePermission }),
      });
      setEmail('');
      message.success('Invitation sent');
      await fetchPermissions();
    } catch {
      message.error('Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  // ── Revoke pending invitation ─────────────────────────────────────────────

  const handleRevoke = async (invId: string) => {
    setRevoking(invId);
    try {
      await apiFetch(`/invitations/${invId}`, { method: 'DELETE' });
      message.success('Invitation revoked');
      await fetchPermissions();
    } catch {
      message.error('Failed to revoke invitation');
    } finally {
      setRevoking(null);
    }
  };

  // ── Change permission ─────────────────────────────────────────────────────

  const permEndpoint = '/entities/permissions';

  const handleChangePermission = async (permId: string, newPermission: string) => {
    try {
      await apiFetch(`${permEndpoint}/${permId}`, {
        method: 'PATCH',
        body: JSON.stringify({ permission: newPermission }),
      });
      await fetchPermissions();
    } catch {
      message.error('Failed to update permission');
    }
  };

  // ── Remove permission ─────────────────────────────────────────────────────

  const handleRemove = async (permId: string) => {
    try {
      await apiFetch(`${permEndpoint}/${permId}`, {
        method: 'DELETE',
      });
      message.success('Access removed');
      await fetchPermissions();
    } catch {
      message.error('Failed to remove access');
    }
  };

  // ── Remove my own access ──────────────────────────────────────────────────

  const handleRemoveSelf = async () => {
    try {
      await workspaceService.leaveEntity(resourceId);
      onClose();
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  // ── Share link ────────────────────────────────────────────────────────────

  const handleCreateLink = async () => {
    try {
      const data = await apiFetch<{ token: string; permission: string }>(`${basePath}/share`, {
        method: 'POST',
        body: JSON.stringify({ permission: 'read' }),
      });
      setShareLink({
        token: data.token,
        url: `/shared/${data.token}`,
        permission: data.permission,
      });
      message.success('Share link created');
    } catch {
      message.error('Failed to create share link');
    }
  };

  const handleDisableLink = async () => {
    try {
      await apiFetch(`${basePath}/share`, {
        method: 'DELETE',
      });
      setShareLink(null);
      message.success('Share link disabled');
    } catch {
      message.error('Failed to disable share link');
    }
  };

  // Build full share URL — on desktop the location origin is `tauri://localhost`,
  // so use the hosted web origin instead.
  const appBaseUrl = shareBaseUrl({
    isDesktop,
    webOrigin: import.meta.env.VITE_DESKTOP_WEB_ORIGIN ?? '',
    locationOrigin: window.location.origin,
  });
  const fullShareUrl = shareLink?.url ? `${appBaseUrl}${shareLink.url}` : '';

  const handleCopyLink = () => {
    if (!fullShareUrl) return;
    navigator.clipboard.writeText(fullShareUrl).then(() => {
      message.success('Link copied');
    });
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  const getDisplayName = (perm: Permission): string => {
    if (perm.name && !perm.name.includes('|')) return perm.name;
    if (perm.email && !perm.email.includes('|')) return perm.email;
    // Fallback for provider_id-as-email (e.g. "google-oauth2|123")
    return perm.email?.split('|')[0] || 'User';
  };

  const getInitials = (perm: Permission): string => {
    const display = getDisplayName(perm);
    const parts = display.split(/[\s@]/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return display.slice(0, 1).toUpperCase();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Share "${resourceTitle}"`}
      footer={null}
      centered
      width={modalWidth}
      destroyOnClose
    >
      {isCollection && (
        <Flex className={styles.collectionNotice}>
          <Text size="sm" type="secondary">Permissions apply to all documents in this collection.</Text>
        </Flex>
      )}

      {!viewerCanManage && viewerAccess && (
        <Flex align="center" justify="space-between" gap="sm" className={styles.collectionNotice} data-testid="viewer-access-banner">
          <Text size="sm" type="secondary">
            You have <Text size="sm" strong>{PERMISSION_LABEL[viewerAccess.permission] ?? viewerAccess.permission}</Text> access
            {viewerAccess.grantedBy ? <> · Shared by <Text size="sm" strong>{viewerAccess.grantedBy.name}</Text></> : null}
          </Text>
          {canRemoveSelf(viewerAccess) && (
            <Popconfirm
              title="Remove my access?"
              description={removeSelfConfirmDescription(resourceType, viewerAccess.removeSelfItemCount)}
              onConfirm={handleRemoveSelf}
              okDanger
              okText="Remove"
            >
              <Button variant="text" size="sm" danger>
                Remove my access
              </Button>
            </Popconfirm>
          )}
        </Flex>
      )}

      {/* Section 1 — Add people (only shown to users who can manage permissions) */}
      {viewerCanManage && (
        <Flex gap="sm" align="center" className={styles.inviteRow}>
          <Input
            aria-label="Email address"
            name="email"
            autoComplete="email"
            spellCheck={false}
            label="Add people by email..."
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleInvite(); }}
            className={styles.emailInput}
            size="sm"
            variant="default"
          />
          <PermissionSelect
            value={invitePermission}
            onChange={setInvitePermission}
            variant="default"
          />
          <Button
            variant="primary"
            size="sm"
            pressEffect="glow"
            onClick={handleInvite}
            disabled={!email.trim() || inviting}
            loading={inviting}
          >
            Invite
          </Button>
        </Flex>
      )}

      <Divider className={styles.divider} />

      {/* Section 2 — People with access */}
      <Text size="sm" strong className={styles.sectionTitle}>
        People with access
      </Text>

      {loading ? (
        <Flex vertical gap="sm">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton
              key={i}
              active
              avatar={{ shape: 'circle' }}
              title={false}
              paragraph={{ rows: 1 }}
              data-testid="permission-skeleton"
            />
          ))}
        </Flex>
      ) : permissions.length === 0 ? (
        <Text size="sm" type="secondary" className={styles.emptyText}>
          {viewerCanManage ? 'No one else has access yet.' : 'Unable to load members.'}
        </Text>
      ) : (
        <List size="small" className={styles.memberList}>
          {permissions.map((perm) => (
            <List.Item key={perm.id}>
              <Flex align="center" gap="sm" className={styles.memberRow}>
                <Avatar size="sm" src={perm.avatarUrl} className={styles.avatar} style={{ '--member-avatar-bg': 'var(--accent)' } as React.CSSProperties}>
                  {getInitials(perm)}
                </Avatar>
                <Flex vertical className={styles.memberInfo}>
                  <Flex align="center" gap="xs">
                    <Text size="sm" ellipsis>{getDisplayName(perm)}</Text>
                    {!viewerCanManage && perm.email === currentUserEmail && (
                      <Text size="xs" type="secondary" data-testid="you-label">(you)</Text>
                    )}
                  </Flex>
                  {perm.name && perm.email && !perm.email.includes('|') && (
                    <Text size="xs" type="secondary" ellipsis>{perm.email}</Text>
                  )}
                </Flex>
                {perm.isOwner ? (
                  <Tag color="success" className={styles.ownerTag}>Owner</Tag>
                ) : perm.email === currentUserEmail ? (
                  // Viewer sees their own row as a static tag — admins cannot
                  // demote or remove themselves from the dialog.
                  <Tag className={styles.ownerTag} data-testid="permission-label-tag">
                    {PERMISSION_LABEL[perm.permission] ?? perm.permission}
                  </Tag>
                ) : viewerCanManage ? (
                  <Flex gap="xs" align="center">
                    <PermissionSelect
                      value={perm.permission}
                      onChange={(v) => handleChangePermission(perm.id, v)}
                    />
                    <Popconfirm
                      title="Remove access?"
                      description="This person will no longer be able to access this document."
                      onConfirm={() => handleRemove(perm.id)}
                      okDanger
                      okText="Remove"
                    >
                      <Button variant="text" size="sm" danger aria-label={`Remove access for ${getDisplayName(perm)}`}>
                        <Icon name="delete" size={14} />
                      </Button>
                    </Popconfirm>
                  </Flex>
                ) : (
                  <Tag className={styles.ownerTag} data-testid="permission-label-tag">
                    {PERMISSION_LABEL[perm.permission] ?? perm.permission}
                  </Tag>
                )}
              </Flex>
            </List.Item>
          ))}
        </List>
      )}

      {pendingInvitations.length > 0 && (
        <>
          <Divider className={styles.divider} />
          <PendingInvitationsList
            invitations={pendingInvitations}
            onRevoke={handleRevoke}
            revoking={revoking}
          />
        </>
      )}

      {!isCollection && <Divider className={styles.divider} />}

      {/* Section 3 — Share link (documents only) */}
      {!isCollection && (
        <>
          <Text size="sm" strong className={styles.sectionTitle}>
            Share link
          </Text>

          {shareLink ? (
            <Flex vertical gap="sm">
              <Flex gap="sm" align="center">
                <Input
                  aria-label="Share link"
                  name="share-link"
                  value={fullShareUrl}
                  readOnly
                  size="sm"
                  className={styles.linkInput}
                  prefix={<Icon name="link" size={14} />}
                />
                <Tooltip title="Copy link">
                  <Button variant="default" size="sm" onClick={handleCopyLink} aria-label="Copy link">
                    <Icon name="content_copy" size={14} />
                  </Button>
                </Tooltip>
              </Flex>
              {viewerCanManage && (
                <Popconfirm
                  title="Disable share link?"
                  description="Anyone with this link will lose access."
                  onConfirm={handleDisableLink}
                  okDanger
                  okText="Disable"
                >
                  <Button variant="text" size="sm" danger>
                    Disable link
                  </Button>
                </Popconfirm>
              )}
            </Flex>
          ) : viewerCanManage ? (
            <Button
              variant="default"
              size="sm"
              onClick={handleCreateLink}
              icon={<Icon name="link" size={14} />}
            >
              Create share link
            </Button>
          ) : null}
        </>
      )}
    </Modal>
  );
}
